import styled from "styled-components";
import Axios from "axios";
import type {
  CurrencyCode,
  IDiscountCodesSchema,
  MethodsOfUseForm,
} from "../../types/types";
import {
  ButtonWithConfirmDialog,
  DeleteButton,
  TextButton,
} from "../Buttons/Buttons";
import { Row } from "../Layout/Layout";
import { TextField } from "../TextFields/TextFields";
import { formatPrice } from "../../util/util-components";
import { t } from "i18next";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";
import { useFormWrapper, useStoreState } from "../../util/util";

const BrandedTextButton = styled(TextButton)`
  color: ${({ theme }) => theme.brandColor};
  font-size: ${({ theme }) => theme.fontSizes.small};
  padding-left: 0;
`;

const ItemText = styled.span`
  color: ${({ theme }) => theme.secondaryTextColor};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  margin-right: 30px;
`;

const NumberText = styled.span`
  color: ${({ theme }) => theme.primaryTextColor};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
`;

export const DiscountCode = ({
  currencyCode,
  methodsOfUseForm,
  appliedCode,
  handleValidDiscountCode,
  editable = false,
  setDiscountAmount,
  setDiscountType,
  buyerTenantId,
  totalDiscount,
}: {
  currencyCode: CurrencyCode;
  methodsOfUseForm: MethodsOfUseForm;
  appliedCode?: IDiscountCodesSchema | null;
  handleValidDiscountCode?: (discountCode: string | null) => void;
  editable?: boolean;
  setDiscountAmount?: Dispatch<SetStateAction<number>>;
  setDiscountType?: Dispatch<SetStateAction<"flat" | "percentage">>;
  buyerTenantId?: string;
  totalDiscount?: string;
}): JSX.Element => {
  const { storefront_id } = useStoreState();
  const [showPromoCodeField, setShowPromoCodeField] = useState(false);
  const [inputCode, setInputCode] = useState<string | null>(
    appliedCode?.discount_code ?? null
  );
  const [inputError, setInputError] = useState<string>();
  const [validPromoCode, setValidPromoCode] =
    useState<IDiscountCodesSchema | null | undefined>(appliedCode);

  const { setError, clearErrors } = methodsOfUseForm;
  const [appliedDiscount, setAppliedDiscount] = useState<string>();
  const { register } = useFormWrapper();

  useEffect(() => {
    if (
      inputCode &&
      handleValidDiscountCode &&
      inputCode !== validPromoCode?.discount_code
    ) {
      const validateCode = setTimeout(() => {
        let url = `/v2/storefronts/${storefront_id}/discount_codes/validate/${inputCode}`;
        if (buyerTenantId) {
          url += `?buyer_tenant_id=${buyerTenantId}`;
        }
        Axios.get(url)
          .then((response) => {
            setInputError(undefined);
            setValidPromoCode(response.data);
            handleValidDiscountCode(inputCode);
            setDiscountAmount?.(Number(response.data.amount));
            setDiscountType?.(response.data.type);
          })
          .catch((error) => {
            if (appliedCode && inputCode !== validPromoCode?.discount_code) {
              handleValidDiscountCode(null);
              setValidPromoCode(null);
            }
            if (inputCode !== appliedCode?.discount_code) {
              setInputError(
                error.response.data.message || "Invalid Promo Code"
              );
              setValidPromoCode(null);
            }
            setDiscountAmount?.(0);
          });
      }, 2000);

      return () => clearTimeout(validateCode);
    }
  }, [
    inputCode,
    storefront_id,
    validPromoCode,
    appliedCode,
    buyerTenantId,
    handleValidDiscountCode,
    setDiscountAmount,
    setDiscountType,
  ]);

  useEffect(() => {
    if (inputCode === "" || inputCode === appliedCode?.discount_code) {
      setInputError(undefined);
      setDiscountAmount?.(0);
      if (inputCode === appliedCode?.discount_code) {
        setValidPromoCode(appliedCode);
      }
    }
  }, [inputCode, appliedCode, setDiscountAmount]);

  useEffect(() => {
    if (inputError) {
      setError("discount_code", {
        type: "manual",
        message: inputError,
      });
    } else {
      clearErrors("discount_code");
    }
  }, [inputError, setError, clearErrors]);

  useEffect(() => {
    methodsOfUseForm.register("discount_code");
  }, [methodsOfUseForm]);

  useEffect(() => {
    const setAmount = (promoCode: IDiscountCodesSchema) => {
      const discount = totalDiscount
        ? parseFloat(totalDiscount)
        : promoCode.amount;
      const amount =
        promoCode.type === "flat"
          ? discount > 0 && discount !== promoCode.amount
            ? formatPrice(discount, currencyCode)
            : formatPrice(promoCode.amount, currencyCode)
          : `${promoCode.amount}%`;
      setAppliedDiscount(amount);
    };

    if (!editable && appliedCode) {
      setAmount(appliedCode);
    }
    if (validPromoCode) {
      setAmount(validPromoCode);
    }
  }, [
    totalDiscount,
    appliedCode,
    validPromoCode,
    currencyCode,
    editable,
    setDiscountAmount,
  ]);

  return (
    <>
      {(!appliedCode || editable) && (
        <>
          {!showPromoCodeField && !validPromoCode && (
            <BrandedTextButton
              type="button"
              onClick={() => setShowPromoCodeField(true)}
            >
              {t("Add Promo Code")}
            </BrandedTextButton>
          )}
          {(showPromoCodeField || (editable && appliedCode)) && (
            <>
              <Row key={"discount_code"}>
                <TextField
                  name="discount_code"
                  extraSmall={true}
                  label={t("Promo Code")}
                  defaultValue={validPromoCode?.discount_code}
                  onChange={(e) => {
                    setInputCode(e.target.value);
                  }}
                  theref={register()}
                  type="text"
                  formState={methodsOfUseForm.formState}
                  errors={methodsOfUseForm.errors}
                />
                {!inputCode && (
                  <DeleteButton
                    onClick={() => {
                      setInputCode(null);
                      setInputError(undefined);
                      setShowPromoCodeField(false);
                      setValidPromoCode(null);
                      if (appliedCode && handleValidDiscountCode) {
                        handleValidDiscountCode(null);
                      }
                    }}
                  />
                )}
                {inputCode && (
                  <ButtonWithConfirmDialog
                    Button={DeleteButton}
                    confirmMessage={
                      "Are you sure you want to remove promo code?"
                    }
                    handleConfirm={() => {
                      setInputCode(null);
                      setInputError(undefined);
                      setShowPromoCodeField(false);
                      setValidPromoCode(null);
                      setDiscountAmount?.(0);
                      if (appliedCode && handleValidDiscountCode) {
                        handleValidDiscountCode(null);
                      }
                    }}
                    type="button"
                    testid={`delete-discount-code`}
                  />
                )}
              </Row>
              {validPromoCode && (
                <div style={{ marginTop: "5px" }}>
                  <Row key={"discount_code"}>
                    <ItemText>{t("Applied Discount")}</ItemText>
                    <NumberText data-testid={`discount_code_value`}>
                      {appliedDiscount}
                    </NumberText>
                  </Row>
                </div>
              )}
            </>
          )}
        </>
      )}
      {appliedCode && !editable && (
        <div style={{ marginTop: "5px" }}>
          <Row key={"applied_code"}>
            <ItemText>{t("Promo Code")}</ItemText>
            <NumberText data-testid={`discount_code`}>
              {appliedCode.discount_code}
            </NumberText>
          </Row>
          <Row key={"applied_discount_code"}>
            <ItemText>{t("Applied Discount")}</ItemText>
            <NumberText data-testid={`discount_code_value`}>
              {appliedDiscount}
            </NumberText>
          </Row>
        </div>
      )}
    </>
  );
};
